import React, { useState } from "react";

import { RcFile } from "antd/es/upload";
import moment from "moment";

import { updatePlatformContract } from "services";

import { handleS3Download, useGetPlatformContractsAdmin } from "utils";

import { LANGUAGE_CODE, MARKETS } from "consts";

import { PlatformContract } from "models";

import { AdminHead, Flex, Icon, IconButton, MainPage, Option, Select, Tag, Tooltip, Upload } from "components";

const handleUpload = async (file: RcFile, countryCode: string, languageCode: string) => {
  await updatePlatformContract({
    file,
    countryCode,
    languageCode,
  });

  return false;
};

const ContractRow = ({ contract }: { contract: PlatformContract }) => (
  <>
    <Flex align="center" justify="space-between">
      <div>
        {!contract.replacedById && (
          <>
            <Tag colour="green" text="Active" />
            <br />
          </>
        )}
        <span className={contract.replacedById ? "grey-text" : ""}>ID: {contract.id}</span>
        <br />
        <span className={contract.replacedById ? "grey-text" : ""}>Language: {contract.languageCode.toLowerCase()}</span>
        <br />
        <span className={contract.replacedById ? "grey-text" : ""}>Uploaded at: {moment(contract.createdAt).format("DD.MM.YYYY")}</span>
      </div>
      <Flex align="center">
        <Icon className="margin-left-100 pointer" iconName="tabler/download" onClick={() => handleS3Download(contract.link)} />
        {!contract.replacedById && (
          <Upload
            name="file"
            showUploadList={false}
            action=""
            beforeUpload={(v) => handleUpload(v, contract.countryCode, contract.languageCode)}
            className="auto-height contract-upload small"
          >
            <Tooltip color="#db3804" title="Replace">
              <IconButton iconName="tabler/replace" />
            </Tooltip>
          </Upload>
        )}
      </Flex>
    </Flex>
    <hr className="margin-bottom-100" />
  </>
);

const Market = ({ platformContracts, countryCode }: { platformContracts?: PlatformContract[]; countryCode: string }) => {
  const existingContracts = platformContracts?.filter((contract) => contract.countryCode === countryCode) ?? [];

  return (
    <div className="margin-bottom-400" key={countryCode}>
      <h3 className="margin-bottom-200">{`Platform contracts: ${countryCode}`}</h3>
      <div className="margin-bottom-200">{existingContracts.map((contract) => <ContractRow contract={contract} key={contract.id} />) ?? null}</div>
      {!existingContracts?.length && <p>No contract uploaded"</p>}
    </div>
  );
};

export const PlatformTerms = () => {
  const { data: platformContracts, isLoading } = useGetPlatformContractsAdmin();

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");

  const defaultLanguages: Record<string, string> = {
    DE: LANGUAGE_CODE.GERMAN,
    PL: LANGUAGE_CODE.POLISH,
    GB: LANGUAGE_CODE.ENGLISH_GB,
    US: LANGUAGE_CODE.ENGLISH_US,
    CZ: LANGUAGE_CODE.CZECH,
    SK: LANGUAGE_CODE.SLOVAK,
  };

  const existingMarkets = platformContracts?.reduce<string[]>((acc, { countryCode }) => (acc.includes(countryCode) ? acc : [...acc, countryCode]), []) ?? [];
  const newMarkets = Object.values(MARKETS).filter((market) => !platformContracts?.find((contract) => contract.countryCode === market));

  return (
    <MainPage loading={isLoading} pageName={"admin"}>
      <AdminHead
        headline="Update platform terms"
        description={
          <>Replacing an existing contract will mean that all farmers who have signed that contract will see a popup where they are shown the new contract.</>
        }
      />
      <section className="container tiny" style={{ maxWidth: "768px" }}>
        {existingMarkets.map((countryCode) => (
          <Market key={countryCode} platformContracts={platformContracts} countryCode={countryCode} />
        ))}
        <h3 className="margin-bottom-200">Add terms & conditions for a new market</h3>
        <Flex align="center">
          {/* @ts-expect-error need to convert this to TS */}
          <Select className="width-100" label="Select a new market" rules={[{ required: false }]} value={selectedCountryCode} onChange={setSelectedCountryCode}>
            {newMarkets.map((market) => (
              <Option key={market} value={market}>
                {market}
              </Option>
            ))}
          </Select>
          <Upload
            disabled={!selectedCountryCode}
            name="file"
            showUploadList={false}
            beforeUpload={(v) => handleUpload(v, selectedCountryCode, defaultLanguages[selectedCountryCode])}
            className="auto-height contract-upload small"
          >
            <Tooltip color="#db3804" title="Upload terms for new market">
              <IconButton iconName="tabler/upload" disabled={!selectedCountryCode} />
            </Tooltip>
          </Upload>
        </Flex>
      </section>
    </MainPage>
  );
};
